import { useMemo, useRef, useState, useEffect } from "react";
import { Spin } from "antd";
import "./index.scss";
import { useVirtualizer } from "@tanstack/react-virtual";

const VirtualList = ({ items, renderItem }: any) => {
  const containerRef = useRef(null);
  const maxItemRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const itemsSignature = useMemo(() => {
    return items.map((item: any, idx: any) => `${item.value}${idx}`).join("|");
  }, [items]);

  const longestItem = useMemo(
    () =>
      items.reduce(
        (prev: any, curr: any) =>
          String(prev.text || "").trim().length > String(curr.text || "").trim().length
            ? prev
            : curr,
        { text: "", value: "" }
      ),
    [itemsSignature]
  );

  const originalList = useMemo(() => items, [itemsSignature]);

  const virtualizer = useVirtualizer({
    count: originalList.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 34,
    overscan: 5,
    measureElement: (element, _roEntry, instance) => {
      const cachedSize = instance.measurementsCache[instance.indexFromElement(element)].size;
      const rect = element.getBoundingClientRect();
      if (rect.height > 0 && rect.width > 0) {
        return Math.round(rect.height);
      }
      return cachedSize;
    },
  });

  const renderItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isMouseDown = false;
    // @ts-ignore
    let startScrollTop = container.scrollTop;
    let scrollTimer: any = null;

    const handleMouseDown = () => {
      isMouseDown = true;
    };

    const handleMouseUp = () => {
      isMouseDown = false;
      setLoading(false);
    };

    const handleScroll = () => {
      if (isMouseDown) {
        // @ts-ignore
        const scrollDistance = Math.abs(container.scrollTop - startScrollTop);
        if (scrollDistance > 500) {
          setLoading(true);
        }
        // @ts-ignore
        startScrollTop = container.scrollTop;

        if (scrollTimer) {
          clearTimeout(scrollTimer);
        }

        scrollTimer = setTimeout(() => {
          setLoading(false);
        }, 150);
      }
    };

    const handleWheel = () => {
      setLoading(false);
    };

    // @ts-ignore
    container.addEventListener("mousedown", handleMouseDown);
    // @ts-ignore
    container.addEventListener("mouseup", handleMouseUp);
    // @ts-ignore
    container.addEventListener("mouseleave", handleMouseUp);
    // @ts-ignore
    container.addEventListener("scroll", handleScroll);
    // @ts-ignore
    container.addEventListener("wheel", handleWheel);

    return () => {
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
      // @ts-ignore
      container.removeEventListener("mousedown", handleMouseDown);
      // @ts-ignore
      container.removeEventListener("mouseup", handleMouseUp);
      // @ts-ignore
      container.removeEventListener("mouseleave", handleMouseUp);
      // @ts-ignore
      container.removeEventListener("scroll", handleScroll);
      // @ts-ignore
      container.removeEventListener("wheel", handleWheel);
    };
  }, []);

  return (
    <Spin spinning={loading}>
      <div ref={containerRef} className="virtualScrollContainer">
        {/* hidden item for caculate the max width */}
        <div ref={maxItemRef} className="measureItem">
          {renderItem(longestItem)}
        </div>
        <div
          style={{
            height: virtualizer.getTotalSize(),
            width: "100%",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${renderItems[0]?.start ?? 0}px)`,
            }}
          >
            {renderItems.map((virtualRow) => (
              <div
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
              >
                {renderItem(originalList[virtualRow.index])}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Spin>
  );
};
export default VirtualList;
